import { RFQ_TERMS_FIELDS, RFQ_TERMS_INITIAL } from '@euroasiann/shared';
import type { IdpAutofillPayload } from '@euroasiann/shared/idp';
import {
  DEFAULT_CONTACT_PHONE_COUNTRY_CODE,
  hydrateContactPhoneFields,
} from './contact-phone';
import type { RfqExcelFormData, RfqExcelItem } from './rfq-excel.types';

export const RFQ_IMPORT_INITIAL_FORM_DATA = {
  revisionNo: '00',
  currency: '',
  quoteDeadline: '',
  quoteValidityDays: '30',
  urgencyLevel: 'Normal',
  requisitionRef: '',

  vesselName: '',
  imoNo: '',
  equipmentTags: '',
  category: '',
  subCategory: '',
  brand: '',
  model: '',
  department: '' as
    | 'engine'
    | 'deck'
    | 'cabin'
    | 'operations'
    | 'safety'
    | 'other'
    | 'electrical'
    | 'navigation'
    | 'environmental'
    | '',
  estimatedTotalCost: '',
  remarks: '',

  deliveryRequiredAt: 'port',
  deliveryType: 'berth',
  portId: '',
  portName: '',
  portCountry: '',
  portEtaDate: '',
  portAgentId: '',
  portAgentName: '',
  portAddress: '',
  portLatitude: '',
  portLongitude: '',
  warehouseAddress1: '',
  warehousePincode: '',
  warehouseCity: '',
  warehouseState: '',
  warehouseCountry: '',
  vesselCurrentLocation: '',
  vesselLatitude: '',
  vesselLongitude: '',
  vesselNextPort: '',
  vesselEtaWindow: '',
  vesselInstructions: '',

  typeOfIncoterms: '',
  typeOfLogisticContainer: '',
  ...RFQ_TERMS_INITIAL,
  leadDate: '',
  partialQuoteAllowed: 'no',
  splitOrderAllowed: 'no',

  contactName: '',
  contactEmail: '',
  contactPhoneCountryCode: DEFAULT_CONTACT_PHONE_COUNTRY_CODE,
  contactPhone: '',
  contactDepartment: 'Procurement',

  requiredPaymentTerms: '30 Days Credit',
  requiredCertificates: [] as string[],
  requiredDocuments: [] as string[],
  packingInstructions: '',
  partialDeliveryAllowed: 'yes',

  selectedVendorIds: [] as string[],
  externalVendorInvites: [] as Array<{ email: string; companyName?: string }>,
};

/** Form shape used by Create RFQ import apply (Excel + PDF). */
export type RfqImportFormData = typeof RFQ_IMPORT_INITIAL_FORM_DATA;

export const RFQ_IMPORT_INITIAL_ITEMS: RfqExcelItem[] = [
  {
    id: '1',
    impaNo: '',
    itemDescription: '',
    drawingNumber: '',
    partNo: '',
    altPartNo: '',
    modelSerial: '',
    positionNo: '',
    dimensions: '',
    requiredQuantity: '',
    uom: '',
    quality: '',
    generalRemark: '',
    deliveryPriority: 'normal',
  },
];

export interface ApplyRfqImportInput {
  formPatch: Partial<RfqExcelFormData> | Record<string, unknown>;
  items: RfqExcelItem[];
  /** PMS job no / requisition fallback when patch has no requisitionRef. */
  requisitionRefFallback?: string;
  initialFormData?: RfqImportFormData;
  initialItems?: RfqExcelItem[];
}

export interface ApplyRfqImportResult {
  formData: RfqImportFormData & Record<string, unknown>;
  items: RfqExcelItem[];
}

/**
 * Shared Excel/PDF import apply: reset to initials, merge patch, hydrate phone,
 * resolve requisitionRef, and use imported items or a blank starter row.
 * Does not touch attachments.
 */
export function applyRfqImportToForm(input: ApplyRfqImportInput): ApplyRfqImportResult {
  const initialFormData = input.initialFormData ?? RFQ_IMPORT_INITIAL_FORM_DATA;
  const initialItems = input.initialItems ?? RFQ_IMPORT_INITIAL_ITEMS;
  const formPatch = (input.formPatch ?? {}) as Record<string, unknown>;

  const requisitionFromPatch =
    typeof formPatch.requisitionRef === 'string' ? formPatch.requisitionRef.trim() : '';

  const formData = {
    ...initialFormData,
    ...formPatch,
    ...hydrateContactPhoneFields(formPatch),
    requisitionRef:
      requisitionFromPatch ||
      (input.requisitionRefFallback?.trim() || '') ||
      initialFormData.requisitionRef,
  };

  const items =
    Array.isArray(input.items) && input.items.length > 0 ? input.items : initialItems;

  return { formData, items };
}

const IDP_SCALAR_FIELD_KEYS = [
  'vesselName',
  'imoNo',
  'requisitionRef',
  'category',
  'subCategory',
  'brand',
  'model',
  'equipmentTags',
  'currency',
  'quoteDeadline',
  'quoteValidityDays',
  'urgencyLevel',
  'department',
  'estimatedTotalCost',
  'contactName',
  'contactEmail',
  'contactPhone',
  'contactDepartment',
  'deliveryRequiredAt',
  'deliveryType',
  'portName',
  'portCountry',
  'portAgentName',
  'portAddress',
  'portEtaDate',
  'warehouseAddress1',
  'warehousePincode',
  'warehouseCity',
  'warehouseState',
  'warehouseCountry',
  'partialQuoteAllowed',
  'splitOrderAllowed',
  'requiredPaymentTerms',
  'partialDeliveryAllowed',
  'packingInstructions',
  'remarks',
  'leadDate',
  'revisionNo',
  'vendors',
] as const;

/** Alias keys from IDP payloads → Create RFQ form keys. */
const IDP_FIELD_ALIASES: Array<{ formKey: string; fieldKey: string }> = [
  { formKey: 'contactEmail', fieldKey: 'email' },
  { formKey: 'contactPhone', fieldKey: 'phone' },
  { formKey: 'contactName', fieldKey: 'contactPerson' },
  { formKey: 'vendors', fieldKey: 'vendor' },
  { formKey: 'vendors', fieldKey: 'vendorName' },
  { formKey: 'vendors', fieldKey: 'vendorNames' },
  { formKey: 'portName', fieldKey: 'port' },
  { formKey: 'portName', fieldKey: 'supplyPort' },
  { formKey: 'portAgentName', fieldKey: 'portAgent' },
  { formKey: 'portAgentName', fieldKey: 'agentName' },
  { formKey: 'subCategory', fieldKey: 'subcategory' },
  { formKey: 'imoNo', fieldKey: 'imoNumber' },
  { formKey: 'vesselName', fieldKey: 'vessel' },
  { formKey: 'quoteValidityDays', fieldKey: 'validity' },
  { formKey: 'quoteValidityDays', fieldKey: 'quoteValidity' },
  { formKey: 'estimatedTotalCost', fieldKey: 'budget' },
  { formKey: 'estimatedTotalCost', fieldKey: 'estimatedCost' },
];

function readIdpScalar(
  fields: IdpAutofillPayload['fields'],
  fieldKey: string
): string | undefined {
  const val = fields[fieldKey]?.value;
  if (val == null) return undefined;
  if (Array.isArray(val)) {
    const joined = val.map((v) => String(v ?? '').trim()).filter(Boolean).join(', ');
    return joined || undefined;
  }
  const text = String(val).trim();
  return text || undefined;
}

function readIdpStringList(
  fields: IdpAutofillPayload['fields'],
  fieldKey: string
): string[] | undefined {
  const raw = fields[fieldKey]?.value;
  if (raw == null) return undefined;
  if (Array.isArray(raw)) {
    const list = raw.map((v) => String(v ?? '').trim()).filter(Boolean);
    return list.length ? list : undefined;
  }
  const text = String(raw).trim();
  if (!text) return undefined;
  const list = text
    .split(/[,;]+/)
    .map((s) => s.trim())
    .filter(Boolean);
  return list.length ? list : undefined;
}

function normalizeIdpItems(rawItems: unknown): RfqExcelItem[] {
  if (!Array.isArray(rawItems) || rawItems.length === 0) return [];

  return rawItems.map((item: Record<string, unknown>, index: number) => {
    const qtyRaw = item.requiredQuantity ?? item.qty;
    const quantity =
      qtyRaw == null || String(qtyRaw).trim() === '' ? '' : String(qtyRaw).trim();

    // Preserve existing intended defaults only: empty position falls back to row index;
    // priority falls back to normal when omitted (existing RFQ item behavior).
    const positionRaw = item.positionNo;
    const positionNo =
      positionRaw == null || String(positionRaw).trim() === ''
        ? String(index + 1)
        : String(positionRaw).trim();

    const priorityRaw = item.deliveryPriority ?? item.priority;
    const deliveryPriority =
      priorityRaw == null || String(priorityRaw).trim() === ''
        ? 'normal'
        : String(priorityRaw).trim();

    return {
      id: String(item.id || index + 1),
      impaNo: String(item.impaNo || item.msfNumber || item.msfNo || ''),
      itemDescription: String(item.itemDescription || item.description || ''),
      drawingNumber: String(item.drawingNumber || item.drawingNo || ''),
      partNo: String(item.partNo || ''),
      altPartNo: String(item.altPartNo || ''),
      modelSerial: String(item.modelSerial || ''),
      positionNo,
      dimensions: String(item.dimensions || ''),
      requiredQuantity: quantity,
      uom: String(item.uom || ''),
      quality: String(item.quality || ''),
      generalRemark: String(item.generalRemark || item.remarks || ''),
      deliveryPriority,
      expectedDeliveryDate: String(item.expectedDeliveryDate || ''),
    };
  });
}

/**
 * Normalize IDP autofill payload into the same `{ formPatch, items }` shape
 * used by Excel import apply.
 */
export function normalizeIdpAutofillToImport(payload: IdpAutofillPayload): {
  formPatch: Partial<RfqExcelFormData> & Record<string, unknown>;
  items: RfqExcelItem[];
} {
  const formPatch: Record<string, unknown> = {};
  const fields = payload.fields ?? {};

  for (const key of IDP_SCALAR_FIELD_KEYS) {
    const value = readIdpScalar(fields, key);
    if (value !== undefined) formPatch[key] = value;
  }

  for (const { key } of RFQ_TERMS_FIELDS) {
    const value = readIdpScalar(fields, key);
    if (value !== undefined) formPatch[key] = value;
  }

  for (const { formKey, fieldKey } of IDP_FIELD_ALIASES) {
    if (formPatch[formKey] != null && String(formPatch[formKey]).trim()) continue;
    const value = readIdpScalar(fields, fieldKey);
    if (value !== undefined) formPatch[formKey] = value;
  }

  const certificates = readIdpStringList(fields, 'requiredCertificates');
  if (certificates) formPatch.requiredCertificates = certificates;
  const documents = readIdpStringList(fields, 'requiredDocuments');
  if (documents) formPatch.requiredDocuments = documents;

  return {
    formPatch,
    items: normalizeIdpItems(fields.items?.value),
  };
}
